<template>
  <div class="survey-page responses-page">
    <div class="survey-page-header">
      <div class="survey-page-header-left">
        <div class="survey-page-icon teal">
          <i class="bi bi-bar-chart-line"></i>
        </div>
        <div>
          <h5 class="survey-page-title">Survey Responses</h5>
          <p class="survey-page-sub">Feedback collected from post-activity surveys</p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="survey-loading">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div> Loading...
    </div>

    <div v-else-if="!surveys.length" class="survey-empty">
      <div class="survey-empty-icon"><i class="bi bi-inbox"></i></div>
      <strong>No responses yet</strong>
      <p class="text-muted mb-0 mt-1">Responses appear here once participants submit surveys.</p>
    </div>

    <div v-else class="resp-layout">
      <aside class="resp-sidebar">
        <div class="sidebar-label">Activities with responses</div>
        <button
          v-for="s in surveys"
          :key="s.id"
          type="button"
          class="sidebar-item"
          :class="{ active: selectedId === s.id }"
          @click="selectSurvey(s)"
        >
          <div class="si-title">
            {{ s.activity_title || 'Untitled' }}
            <span v-if="s.instance_label || s.name" class="text-muted"> · {{ s.instance_label || s.name }}</span>
          </div>
          <div class="si-meta">
            <span class="survey-pill" :class="s.survey_type === 'online' ? 'online' : 'f2f'">
              {{ s.survey_type === 'online' ? 'Online' : 'F2F' }}
            </span>
            <span>{{ s.response_count }} resp.</span>
          </div>
        </button>
      </aside>

      <main class="resp-main">
        <div v-if="detailLoading" class="survey-loading">
          <div class="spinner-border spinner-border-sm text-primary"></div>
        </div>

        <template v-else-if="selectedSurvey">
          <div class="resp-main-header">
            <div>
              <h6 class="resp-activity-title">
                {{ selectedSurvey.activity_title }}
                <span v-if="selectedSurvey.name" class="text-muted font-weight-normal"> · {{ selectedSurvey.name }}</span>
              </h6>
              <p class="resp-activity-meta">
                {{ responses.length }} response{{ responses.length === 1 ? '' : 's' }}
                <span v-if="selectedSurvey.is_expired" class="survey-pill expired ml-2">Expired</span>
              </p>
            </div>
          </div>

          <div v-if="ratingSummary.length" class="rating-summary-row">
            <div v-for="item in ratingSummary" :key="item.id" class="rating-summary-card">
              <div class="rs-label">{{ item.label }}</div>
              <div class="rs-score">{{ item.avg }}<span class="rs-max">/{{ ratingMax }}</span></div>
              <div class="rs-bar-wrap">
                <div class="rs-bar" :style="{ width: (item.avg / ratingMax * 100) + '%' }"></div>
              </div>
              <div class="rs-count">{{ item.count }} ratings</div>
            </div>
          </div>

          <div v-if="!responses.length" class="text-muted small py-3">No individual responses to show.</div>

          <div v-for="r in responses" :key="r.id" class="response-card">
            <div class="rc-header">
              <div class="rc-avatar">
                <i class="bi bi-person-fill"></i>
              </div>
              <div>
                <div class="rc-name">{{ r.respondent_name || 'Anonymous' }}</div>
                <div class="rc-date">{{ formatDate(r.submitted_at) }}</div>
              </div>
            </div>
            <div class="rc-answers">
              <div v-for="(val, key) in r.answers" :key="key" class="rc-answer">
                <div class="rc-q">{{ labelFor(key) }}</div>
                <div v-if="typeof val === 'number'" class="rc-rating">
                  <span v-for="n in val" :key="n" class="star">★</span>
                  <span class="rc-rating-num">{{ val }}/{{ ratingMax }}</span>
                </div>
                <div v-else class="rc-text">{{ val }}</div>
              </div>
            </div>
          </div>
        </template>
      </main>
    </div>
  </div>
</template>

<script>
import "@/css/surveys.css";
import axios from "axios";
import moment from "moment";

export default {
  name: "SurveyResponses",
  data() {
    return {
      loading: true,
      detailLoading: false,
      surveys: [],
      selectedId: null,
      selectedSurvey: null,
      responses: [],
      questionLabels: {},
      ratingMax: 5,
    };
  },
  computed: {
    ratingSummary() {
      const buckets = {};
      this.responses.forEach((r) => {
        Object.entries(r.answers || {}).forEach(([key, val]) => {
          if (typeof val !== "number") return;
          if (!buckets[key]) buckets[key] = { sum: 0, count: 0 };
          buckets[key].sum += val;
          buckets[key].count += 1;
        });
      });
      return Object.entries(buckets).map(([id, b]) => ({
        id,
        label: this.labelFor(id),
        avg: (b.sum / b.count).toFixed(1),
        count: b.count,
      }));
    },
  },
  mounted() {
    this.loadSettings();
    this.loadList();
  },
  methods: {
    async loadSettings() {
      try {
        const { data } = await axios.get("api/survey-settings");
        const labels = {};
        let maxRating = 5;
        ["online", "face-to-face"].forEach((type) => {
          const cfg = data[type];
          if (!cfg) return;
          maxRating = Math.max(maxRating, cfg.rating_scale_max || 5);
          (cfg.questions || []).forEach((q) => { labels[q.id] = q.label; });
        });
        this.questionLabels = labels;
        this.ratingMax = maxRating;
      } catch (e) { /* non-critical */ }
    },
    async loadList() {
      this.loading = true;
      try {
        const params = {};
        if (this.$route.query.event_id) params.event_id = this.$route.query.event_id;
        const { data } = await axios.get("api/surveys/responses", { params });
        this.surveys = data.surveys || [];
        if (this.surveys.length) {
          let match = null;
          if (this.$route.query.survey_id) {
            match = this.surveys.find((s) => String(s.id) === String(this.$route.query.survey_id));
          }
          if (!match && this.$route.query.event_id) {
            match = this.surveys.find((s) => String(s.calendar_event_id) === String(this.$route.query.event_id));
          }
          this.selectSurvey(match || this.surveys[0]);
        }
      } catch (e) {
        toastr.error("Failed to load survey responses.", "Error");
      } finally {
        this.loading = false;
      }
    },
    async selectSurvey(survey) {
      this.selectedId = survey.id;
      this.selectedSurvey = survey;
      this.detailLoading = true;
      try {
        const { data } = await axios.get(`api/surveys/${survey.id}/responses`);
        this.responses = data.responses || [];
      } catch (e) {
        toastr.error("Failed to load responses.", "Error");
      } finally {
        this.detailLoading = false;
      }
    },
    labelFor(key) { return this.questionLabels[key] || key; },
    formatDate(dt) { return dt ? moment(dt).format("MMM D, YYYY h:mm A") : ""; },
  },
};
</script>

<style scoped>
.responses-page { width: 100%; }

.resp-layout {
  display: grid;
  grid-template-columns: minmax(240px, 280px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

@media (max-width: 767.98px) {
  .resp-layout { grid-template-columns: 1fr; }
}

.resp-sidebar {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 14px;
  padding: 0.65rem;
  box-shadow: 0 4px 20px rgba(43, 58, 85, 0.05);
  max-height: 70vh;
  overflow-y: auto;
}

.sidebar-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9aa5b4;
  padding: 0.35rem 0.5rem 0.5rem;
  font-weight: 600;
}

.sidebar-item {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease;
  margin-bottom: 0.15rem;
}

.sidebar-item:hover,
.sidebar-item.active {
  background: rgba(77, 141, 255, 0.08);
}

.sidebar-item.active { box-shadow: inset 3px 0 0 #4d8dff; }

.si-title {
  font-weight: 600;
  font-size: 0.85rem;
  color: #2b3a55;
  margin-bottom: 0.3rem;
  line-height: 1.3;
}

.si-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #9aa5b4;
}

.resp-main {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(43, 58, 85, 0.05);
  min-height: 300px;
}

.resp-activity-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #2b3a55;
  margin: 0;
}

.resp-activity-meta {
  font-size: 0.82rem;
  color: #768192;
  margin: 0.2rem 0 0;
}

.rating-summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.65rem;
  margin: 1rem 0;
}

.rating-summary-card {
  background: #f8f9fb;
  border-radius: 10px;
  padding: 0.75rem;
}

.rs-label {
  font-size: 0.72rem;
  color: #768192;
  margin-bottom: 0.25rem;
  line-height: 1.3;
}

.rs-score {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2b3a55;
}

.rs-max { font-size: 0.85rem; font-weight: 500; color: #9aa5b4; }

.rs-bar-wrap {
  height: 4px;
  background: rgba(0, 0, 21, 0.08);
  border-radius: 2px;
  margin: 0.4rem 0 0.25rem;
  overflow: hidden;
}

.rs-bar {
  height: 100%;
  background: linear-gradient(90deg, #4d8dff, #2bb6a3);
  border-radius: 2px;
}

.rs-count { font-size: 0.68rem; color: #9aa5b4; }

.response-card {
  border: 1px solid rgba(0, 0, 21, 0.06);
  border-radius: 12px;
  padding: 1rem;
  margin-top: 0.75rem;
}

.rc-header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid rgba(0, 0, 21, 0.05);
}

.rc-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(77, 141, 255, 0.1);
  color: #4d8dff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rc-name { font-weight: 600; font-size: 0.88rem; color: #2b3a55; }
.rc-date { font-size: 0.75rem; color: #9aa5b4; }

.rc-answers { display: flex; flex-direction: column; gap: 0.5rem; }

.rc-q { font-size: 0.72rem; color: #768192; margin-bottom: 0.15rem; }
.rc-rating .star { color: #f59f00; font-size: 0.95rem; }
.rc-rating-num { margin-left: 0.35rem; font-size: 0.8rem; color: #4a5568; font-weight: 600; }
.rc-text { font-size: 0.88rem; color: #2b3a55; white-space: pre-wrap; line-height: 1.45; }
</style>
